import React, { useEffect, useState } from "react";
import { Plus, MapPin, Wallet } from "lucide-react";
import api, { formatApiError, inr } from "../../api";

const TYPES = ["Flat", "House", "Commercial"];

export default function BuyerDashboard() {
  const [reqs, setReqs] = useState([]);
  const [cities, setCities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", preferred_property_type: "Flat",
    listing_preference: "Rent", budget_min: "", budget_max: "",
    preferred_city: "Pune", preferred_area: "", notes: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/my/requirements");
      setReqs(data.requirements);
    } catch (e) {}
  };
  useEffect(() => {
    load();
    api.get("/meta/cities").then((r) => setCities(r.data.cities)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const areas = cities.find((c) => c.name === form.preferred_city)?.areas || [];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/requirements", {
        ...form,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
      });
      setShowForm(false);
      setForm({ ...form, notes: "" });
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    }
  };

  const input = "w-full bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-brand-500";
  const label = "block text-sm text-neutral-600 mb-1.5";

  return (
    <div data-testid="buyer-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-neutral-900">My Requirements <span className="text-neutral-400 text-lg">({reqs.length})</span></h2>
        {!showForm && (
          <button onClick={() => setShowForm(true)} data-testid="add-requirement-btn"
            className="flex items-center gap-2 px-5 py-2.5 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl hover:shadow-soft transition-all active:scale-95">
            <Plus className="w-5 h-5" /> Submit Requirement
          </button>
        )}
      </div>

      {showForm ? (
        <form onSubmit={submit} className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-6 md:p-8 space-y-5" data-testid="requirement-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className={label}>Name *</label><input required className={input} value={form.name} onChange={set("name")} data-testid="req-name" /></div>
            <div><label className={label}>Email *</label><input required type="email" className={input} value={form.email} onChange={set("email")} data-testid="req-email" /></div>
            <div><label className={label}>Mobile *</label><input required className={input} value={form.mobile} onChange={set("mobile")} data-testid="req-mobile" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Preferred Type</label>
              <select className={input} value={form.preferred_property_type} onChange={set("preferred_property_type")} data-testid="req-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Rent / Buy</label>
              <select className={input} value={form.listing_preference} onChange={set("listing_preference")} data-testid="req-listing">
                <option value="Rent">Rent</option>
                <option value="Buy">Buy</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Budget Min (₹)</label><input type="number" className={input} value={form.budget_min} onChange={set("budget_min")} data-testid="req-budget-min" /></div>
            <div><label className={label}>Budget Max (₹)</label><input type="number" className={input} value={form.budget_max} onChange={set("budget_max")} data-testid="req-budget-max" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Preferred City</label>
              <select className={input} value={form.preferred_city} onChange={(e) => setForm({ ...form, preferred_city: e.target.value, preferred_area: "" })} data-testid="req-city">
                {cities.length === 0 && <option value="Pune">Pune</option>}
                {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Preferred Area</label>
              <input list="req-areas" className={input} value={form.preferred_area} onChange={set("preferred_area")} data-testid="req-area" />
              <datalist id="req-areas">{areas.map((a) => <option key={a} value={a} />)}</datalist>
            </div>
          </div>
          <div><label className={label}>Notes</label><textarea rows={3} className={input} value={form.notes} onChange={set("notes")} data-testid="req-notes" /></div>
          {err && <p className="text-red-500 text-sm" data-testid="req-error">{err}</p>}
          <div className="flex gap-3">
            <button type="submit" data-testid="req-submit-btn" className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl active:scale-95">Save Requirement</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 text-neutral-600 border border-neutral-300 rounded-xl hover:text-neutral-900">Cancel</button>
          </div>
        </form>
      ) : reqs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 rounded-2xl" data-testid="no-requirements">
          <p className="text-neutral-500">No requirements yet. Tell us what you're looking for.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {reqs.map((r) => (
            <div key={r.id} className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-5" data-testid={`requirement-${r.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-brand-500 text-white">{r.listing_preference}</span>
                <span className="text-neutral-500 text-sm">{r.preferred_property_type}</span>
              </div>
              <p className="flex items-center gap-1 text-neutral-700"><MapPin className="w-4 h-4 text-brand-500" /> {r.preferred_area || "Any"}, {r.preferred_city}</p>
              <p className="flex items-center gap-1 text-neutral-700 mt-1"><Wallet className="w-4 h-4 text-brand-500" /> {inr(r.budget_min)} – {inr(r.budget_max)}</p>
              {r.notes && <p className="text-neutral-400 text-sm mt-2">{r.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
